import {
    Alert,
    Checkbox,
    FormControlLabel,
    Grid2,
    Icon,
    TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import { DataTypeEnum } from "../../../_common/enum/dataType.enum";
import { FormStatusEnum } from "../../../_common/enum/forms/formStatus.enum";
import { TicketStatusEnum } from "../../../_common/enum/ticketStatus.enum";
import { TicketTypeEnum } from "../../../_common/enum/ticketType.enum";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { getFormatedDate } from "../../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { accountService } from "../../../_services/accounts.service";
import { formService } from "../../../_services/form.service";
import { ticketActionService } from "../../../_services/ticketAction.service";
import { ticketService } from "../../../_services/ticket.service";
import { TicketCloseFollowupDialog } from "./ticket-closeFollowupDialog";

export function TicketActionManage(params: { onSave(): void }) {

    const resetData = () => ({});

    const { id, caseId, ticketType, actionId } = useParams<{ id: string, caseId?: string, ticketType?: string, actionId?: string }>();
    const ticketTypeId = TicketTypeEnum[ticketType as keyof typeof TicketTypeEnum].toString();
    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setIsSaving] = useState(false);
    const [closeFollowup, setCloseFollowup] = useState<{
        open: boolean;
        ticketResultId?: number;
        invoiceNumber?: string;
    }>({ open: false });

    const [isReadOnly, setIsReadOnly] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (actionId != null) fetchData();
        else setData(resetData);
    }, [id, actionId]);

    function fetchData() {
        if (!actionId) return;
        setIsLoading(true);
        return ticketActionService
            .get(actionId || "")
            .then(
                (json) => {
                    setData(json);

                    if (json.isCompleted === true || accountService.isClient)
                        setIsReadOnly(true);
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        const _data = { ...data, ticketId: id };
        const ticketStatusPromise = _data?.isCompleted && id && ticketType
            ? ticketService.get(id, ticketType).then((ticket: any) => ticket?.ticketStatusId).catch(() => undefined)
            : Promise.resolve(undefined);

        return ticketStatusPromise.then((previousStatusId: number | undefined) => ticketActionService
            .submit(_data)
            .then(
                async () => {
                    toastr.success("Saved successfully");
                    const openedFollowup = await promptTicketClosureFollowup(_data, previousStatusId);
                    if (!openedFollowup) {
                        params.onSave();
                    }
                    //navigate(-1);
                },
                handleErrorResponse
            )
            .finally(() => setIsSaving(false)));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    function skipForm() {
        if (!actionId) return;

        setIsSaving(true);
        const _data = { ...data, ticketId: id, isCompleted: true, skipForm: true };
        const ticketStatusPromise = id && ticketType
            ? ticketService.get(id, ticketType).then((ticket: any) => ticket?.ticketStatusId).catch(() => undefined)
            : Promise.resolve(undefined);

        return ticketStatusPromise.then((previousStatusId: number | undefined) => ticketActionService
            .submit(_data)
            .then(
                async () => {
                    toastr.success("Form skipped and action completed");
                    const openedFollowup = await promptTicketClosureFollowup(_data, previousStatusId);
                    if (!openedFollowup) {
                        params.onSave();
                    }
                },
                handleErrorResponse
            )
            .finally(() => setIsSaving(false)));
    }

    async function promptTicketClosureFollowup(submittedAction: any, previousStatusId?: number): Promise<boolean> {
        if (!id || !ticketType || !submittedAction?.isCompleted)
            return false;

        const ticketBefore = previousStatusId != null
            ? { ticketStatusId: previousStatusId }
            : await ticketService.get(id, ticketType);
        const ticketAfter = await ticketService.get(id, ticketType);

        const justClosed = ticketBefore?.ticketStatusId !== TicketStatusEnum.Close
            && ticketAfter?.ticketStatusId === TicketStatusEnum.Close;

        if (!justClosed)
            return false;

        if (ticketAfter?.ticketResultId && ticketAfter?.invoiceNumber)
            return false;

        setCloseFollowup({
            open: true,
            ticketResultId: ticketAfter?.ticketResultId,
            invoiceNumber: ticketAfter?.invoiceNumber
        });
        return true;
    }

    function goToForm() {
        if (data.formId) {
            //window.open(window.location.origin + "/forms/" + data.formId, "_blank");
            navigate("/forms/" + data.formId);
        }
        else {
            setIsLoading(true);
            return formService
                .createAction(+(data.formTemplateId || 0), +(actionId || 0))
                .then(
                    (id: number) => {
                        toastr.success("Form created successfully");
                        setData((_d: any) => ({ ..._d, formId: id }))
                        //window.open(window.location.origin + "/forms/" + id, "_blank");
                        navigate("/forms/" + id);
                    },
                    handleErrorResponse
                )
                .finally(() => setIsLoading(false));
        }
    }

    const canSkipForm = !!data.formTemplateId
        && !data.formId
        && !isReadOnly
        && !accountService.isAgent
        && !accountService.isClient
        && data.formStatusId != FormStatusEnum.Approved;

    return (
        <>
            <ValidatorForm onSubmit={submit} noValidate style={{ padding: "4px" }}>
                <Grid2 container spacing={1}>

                    {!actionId &&
                        <Grid2 size={{ xs: 12 }}>
                            <DataTypeAutoComplete
                                parentId={ticketTypeId}
                                key="actionTypeId"
                                dataType={DataTypeEnum.TicketActionTypes}
                                label="Select Action"
                                hasParent={true}
                                value={data.actionTypeId}
                                name={"actionTypeId"}
                                onChange={handleChange}
                                required={true}
                            />
                        </Grid2>
                    }

                    {!!actionId &&
                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="actionType"
                                name="actionType"
                                label="Action Type"
                                fullWidth
                                disabled
                                value={data.actionTypeName}
                            />
                        </Grid2>
                    }

                    <Grid2 size={{ xs: 12 }}>
                        <StandardTextValidator
                            key="nextActionDate"
                            name="nextActionDate"
                            label="Next Action Date"
                            type="date"
                            fullWidth
                            disabled={isReadOnly || accountService.isAgent}
                            value={data.nextActionDate != null ? getFormatedDate(data.nextActionDate) : ""}
                            onChange={handleChange}
                        />
                    </Grid2>

                    {(!data.formTemplateId || data.formStatusId == FormStatusEnum.Approved) &&
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!data.isCompleted}
                                        onChange={(e) =>
                                            setData((_data: any) => ({
                                                ..._data, isCompleted: e.target.checked
                                            }))
                                        }
                                        name="isCompleted"
                                        disabled={isReadOnly}
                                    />
                                }
                                label="Mark Completed"
                            />
                        </Grid2>
                    }

                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!data.isPublic}
                                    onChange={(e) =>
                                        setData((_data: any) => ({
                                            ..._data, isPublic: e.target.checked
                                        }))
                                    }
                                    name="isPublic"
                                    disabled={isReadOnly || accountService.isAgent}
                                />
                            }
                            label="Make Public"
                        />
                    </Grid2>

                    {!accountService.isAgent && <>
                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                name="comment"
                                label="Comment"
                                fullWidth
                                multiline
                                rows={3}
                                disabled={isReadOnly}
                                value={data.comment}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <TextField
                                name="history"
                                label="Comment History"
                                fullWidth
                                multiline
                                rows={17}
                                disabled
                                value={data.history}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid2>

                    </>
                    }

                    {(!!data.formTemplateId && !!data.formId) &&
                        <Grid2 size={{ xs: 12 }}>
                            {(data.formStatusId == FormStatusEnum.Approved) &&
                                <Alert icon={<Icon>check</Icon>} severity="success">
                                    Form Approved
                                </Alert>
                            }

                            {(data.formStatusId == FormStatusEnum.CompletedAwaitingApproval) &&
                                <Alert icon={<Icon>check</Icon>} severity="warning">
                                    Form submission completed � awaiting approval.
                                </Alert>
                            }
                        </Grid2>
                    }

                    {(!!data.formTemplateId) &&
                        <Grid2 size={canSkipForm ? { xs: 12, md: 6 } : { xs: 12 }}>
                            <StandardButton
                                saving={saving}
                                loading={isLoading}
                                icon="edit"
                                type="button"
                                variant="success"
                                onClick={goToForm}
                            >
                                {(data.formStatusId == FormStatusEnum.Approved) && <>View Form</>}
                                {(data.formStatusId == FormStatusEnum.CompletedAwaitingApproval) && <>View Form</>}
                                {(data.formStatusId != FormStatusEnum.CompletedAwaitingApproval && data.formStatusId != FormStatusEnum.Approved && !!data.formId) && <>Go to the form</>}
                                {(!data.formId) && <>Create the form</>}
                            </StandardButton>
                        </Grid2>
                    }

                    {canSkipForm &&
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <StandardButton
                                saving={saving}
                                loading={isLoading}
                                icon="check"
                                type="button"
                                variant="warning"
                                onClick={skipForm}
                            >
                                Skip Form
                            </StandardButton>
                        </Grid2>
                    }

                    {!isReadOnly &&
                        <Grid2 size={{ xs: 6 }}>
                            <StandardButton
                                saving={saving}
                                loading={isLoading}
                                icon="save"
                                type="submit"
                            >
                                Save
                            </StandardButton>
                        </Grid2>
                    }

                    <Grid2 size={{ xs: !isReadOnly ? 6 : 12 }}>
                        <StandardButton
                            saving={saving}
                            loading={isLoading}
                            icon="block"
                            variant="error"
                            onClick={() => { navigate(-1) }}>
                            Cancel
                        </StandardButton>
                    </Grid2>
                </Grid2>
            </ValidatorForm >
            <TicketCloseFollowupDialog
                open={closeFollowup.open}
                ticketId={id}
                caseId={caseId}
                ticketType={ticketType}
                ticketResultId={closeFollowup.ticketResultId}
                invoiceNumber={closeFollowup.invoiceNumber}
                onUpdated={(payload) => {
                    setCloseFollowup((_state) => ({
                        ..._state,
                        ticketResultId: payload.ticketResultId ?? _state.ticketResultId,
                        invoiceNumber: payload.invoiceNumber ?? _state.invoiceNumber
                    }));
                }}
                onClose={() => {
                    setCloseFollowup((state) => ({ ...state, open: false }));
                    params.onSave();
                }}
                
            />
        </>
    );

}
